import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { MapContainer, TileLayer, GeoJSON, Marker, Popup, Tooltip } from "react-leaflet";
import type { Layer, PathOptions } from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  TILE_URL,
  TILE_LABELS_URL,
  RISK_COLORS,
  buildOriginPinIcon,
  ensureMapStyles,
  InvalidateSizeOnMount,
} from "./mapShared";

const COUNTRY_CENTROIDS: Record<string, { lat: number; lng: number; name: string }> = {
  guinea: { lat: 9.9456, lng: -9.6966, name: "Guinea" },
  "sierra leone": { lat: 8.4606, lng: -11.7799, name: "Sierra Leone" },
  liberia: { lat: 6.4281, lng: -9.4295, name: "Liberia" },
  "democratic republic of the congo": { lat: -4.0383, lng: 21.7587, name: "Democratic Republic of Congo" },
  "dr congo": { lat: -4.0383, lng: 21.7587, name: "Democratic Republic of Congo" },
  drc: { lat: -4.0383, lng: 21.7587, name: "Democratic Republic of Congo" },
  uganda: { lat: 1.3733, lng: 32.2903, name: "Uganda" },
  kenya: { lat: 0.0236, lng: 37.9062, name: "Kenya" },
  nigeria: { lat: 9.082, lng: 8.6753, name: "Nigeria" },
  cameroon: { lat: 3.848, lng: 11.5021, name: "Cameroon" },
  "south sudan": { lat: 6.877, lng: 31.307, name: "South Sudan" },
  ethiopia: { lat: 9.145, lng: 40.4897, name: "Ethiopia" },
  malawi: { lat: -13.2543, lng: 34.3015, name: "Malawi" },
  mozambique: { lat: -18.6657, lng: 35.5296, name: "Mozambique" },
  tanzania: { lat: -6.369, lng: 34.8888, name: "Tanzania" },
  zambia: { lat: -13.1339, lng: 27.8493, name: "Zambia" },
  zimbabwe: { lat: -19.0154, lng: 29.1549, name: "Zimbabwe" },
  "south africa": { lat: -30.5595, lng: 22.9375, name: "South Africa" },
  angola: { lat: -11.2027, lng: 17.8739, name: "Angola" },
  congo: { lat: -0.228, lng: 15.8277, name: "Congo" },
  gabon: { lat: -0.8037, lng: 11.6045, name: "Gabon" },
  "central african republic": { lat: 6.6111, lng: 20.9394, name: "Central African Republic" },
  chad: { lat: 15.4542, lng: 18.7322, name: "Chad" },
  sudan: { lat: 12.8628, lng: 30.2176, name: "Sudan" },
  egypt: { lat: 26.8206, lng: 30.8025, name: "Egypt" },
  ghana: { lat: 7.3697, lng: -5.5825, name: "Ghana" },
  "ivory coast": { lat: 7.54, lng: -5.5471, name: "Côte d'Ivoire" },
  "cote d ivoire": { lat: 7.54, lng: -5.5471, name: "Côte d'Ivoire" },
  burundi: { lat: -3.3731, lng: 29.9189, name: "Burundi" },
  rwanda: { lat: -1.9403, lng: 29.8739, name: "Rwanda" },
  somalia: { lat: 5.1521, lng: 46.1996, name: "Somalia" },
  france: { lat: 46.2276, lng: 2.2137, name: "France" },
  "united states": { lat: 37.0902, lng: -95.7129, name: "United States" },
  "united kingdom": { lat: 55.3781, lng: -3.436, name: "United Kingdom" },
  belgium: { lat: 50.5039, lng: 4.4699, name: "Belgium" },
  germany: { lat: 51.1657, lng: 10.4515, name: "Germany" },
  india: { lat: 20.5937, lng: 78.9629, name: "India" },
  china: { lat: 35.8617, lng: 104.1954, name: "China" },
  "united arab emirates": { lat: 23.4241, lng: 53.8478, name: "United Arab Emirates" },
  uae: { lat: 23.4241, lng: 53.8478, name: "United Arab Emirates" },
};

const GEOJSON_URL =
  "https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson";

let cachedBorders: any = null;
let bordersPromise: Promise<any | null> | null = null;

async function loadBorders(): Promise<any | null> {
  if (cachedBorders) return cachedBorders;
  if (bordersPromise) return bordersPromise;
  bordersPromise = (async () => {
    try {
      const res = await fetch(GEOJSON_URL);
      if (!res.ok) throw new Error(String(res.status));
      cachedBorders = await res.json();
      return cachedBorders;
    } catch {
      return null;
    }
  })();
  return bordersPromise;
}

const norm = (s: string) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const ALIASES: Record<string, string> = {
  drc: "democratic republic of the congo",
  "dr congo": "democratic republic of the congo",
  "democratic republic of congo": "democratic republic of the congo",
  usa: "united states",
  uk: "united kingdom",
  uae: "united arab emirates",
  "ivory coast": "cote d ivoire",
};

const canonicalCountry = (name: string) => {
  const n = norm(name);
  return ALIASES[n] || n;
};

const featureName = (props: any) =>
  String(props?.name || props?.ADMIN || props?.NAME || props?.NAME_EN || "");

const getCentroid = (country: string) => {
  const key = canonicalCountry(country);
  return (
    COUNTRY_CENTROIDS[key] ||
    Object.entries(COUNTRY_CENTROIDS).find(
      ([k]) => key.includes(k) || k.includes(key)
    )?.[1] ||
    null
  );
};

/** Blue screening-volume choropleth (not risk-based). */
const CHORO_BASE = "#2563eb";
const CHORO_ALPHAS = ["22", "44", "66", "99", "cc"];

type HeatBin = { min: number; max: number; alpha: string };

function buildQuantileScale(values: number[]): HeatBin[] {
  const nz = values.filter((v) => v > 0).sort((a, b) => a - b);
  if (!nz.length) return [{ min: 0, max: 0, alpha: "00" }];
  const thresholds = CHORO_ALPHAS.map((_, i) => {
    const rank = Math.ceil(((i + 1) / CHORO_ALPHAS.length) * nz.length) - 1;
    return nz[Math.min(rank, nz.length - 1)];
  });
  const out: HeatBin[] = [];
  let prev = 0;
  for (let i = 0; i < thresholds.length; i++) {
    const max = thresholds[i];
    out.push({ min: prev === 0 ? 1 : prev, max, alpha: CHORO_ALPHAS[i] });
    prev = max;
  }
  return out;
}

function alphaFor(value: number, scale: HeatBin[]): string {
  if (value <= 0) return "00";
  for (const b of scale) {
    if (value <= b.max) return b.alpha;
  }
  return scale[scale.length - 1]?.alpha || "cc";
}

const volumeFill = (count: number, scale: HeatBin[], mode: "light" | "dark") => {
  if (count <= 0) return mode === "dark" ? "#1a2438" : "#eef2f7";
  return `${CHORO_BASE}${alphaFor(count, scale)}`;
};

type MapPoint = {
  country?: string;
  risk?: string;
  count?: number;
  high_risk_count?: number;
  moderate_risk_count?: number;
};

type Props = {
  points?: MapPoint[];
  mode?: "light" | "dark";
  selectedCountry?: string;
  alerts?: any[];
  colors?: {
    tooltipBg?: string;
    tooltipText?: string;
    tooltipTextMuted?: string;
  };
};

const WORLD_CENTER: [number, number] = [10, 20];
const WORLD_ZOOM = 2;

const GeoDashboardMap: React.FC<Props> = ({
  points = [],
  mode = "light",
  colors,
}) => {
  const [borders, setBorders] = useState<any>(cachedBorders);

  useEffect(() => {
    ensureMapStyles();
    let alive = true;
    loadBorders().then((g) => {
      if (alive) setBorders(g);
    });
    return () => {
      alive = false;
    };
  }, []);

  const byCountry = useMemo(() => {
    const m = new Map<string, { count: number; label: string }>();
    points.forEach((p) => {
      if (!p.country) return;
      const key = canonicalCountry(p.country);
      const prev = m.get(key);
      m.set(key, {
        count: (p.count || 0) + (prev?.count || 0),
        label: prev?.label || p.country,
      });
    });
    return m;
  }, [points]);

  const scale = useMemo(
    () => buildQuantileScale(Array.from(byCountry.values()).map((v) => v.count)),
    [byCountry]
  );

  const lookup = (fname: string) => {
    const key = canonicalCountry(fname);
    if (!key) return null;
    for (const [k, v] of byCountry) {
      if (k === key || k.includes(key) || key.includes(k)) return { key: k, ...v };
    }
    return null;
  };

  const pins = useMemo(() => {
    return Array.from(byCountry.entries())
      .map(([key, v]) => {
        if (key === "kenya") return null;
        const c = getCentroid(key);
        if (!c || v.count <= 0) return null;
        return {
          key,
          lat: c.lat,
          lng: c.lng,
          name: v.label || c.name,
          count: v.count,
        };
      })
      .filter(Boolean) as {
      key: string;
      lat: number;
      lng: number;
      name: string;
      count: number;
    }[];
  }, [byCountry]);

  const styleFeature = (feature?: any): PathOptions => {
    const fname = featureName(feature?.properties);
    const key = canonicalCountry(fname);
    if (key === "kenya" || key.includes("kenya")) {
      return {
        fillColor: RISK_COLORS.kenya,
        fillOpacity: mode === "dark" ? 0.45 : 0.4,
        color: RISK_COLORS.kenya,
        weight: 2,
        opacity: 1,
      };
    }
    const hit = lookup(fname);
    if (!hit) {
      return {
        fillColor: mode === "dark" ? "#1a2438" : "#eef2f7",
        fillOpacity: mode === "dark" ? 0.25 : 0.45,
        color: mode === "dark" ? "#2a3a55" : "#c5d0de",
        weight: 0.5,
      };
    }
    return {
      fillColor: volumeFill(hit.count, scale, mode),
      fillOpacity: 0.82,
      color: mode === "dark" ? "#0a1120" : "#ffffff",
      weight: 0.8,
    };
  };

  const onEachFeature = (feature: any, layer: Layer) => {
    const fname = featureName(feature.properties);
    const key = canonicalCountry(fname);
    if (key === "kenya" || key.includes("kenya")) {
      layer.bindTooltip(
        `<strong>Kenya</strong><br/><span style="opacity:.8">Entry destination</span>`,
        { sticky: true, className: "dash-map-tip", direction: "top" }
      );
      return;
    }
    const hit = lookup(fname);
    if (!hit) return;
    layer.bindTooltip(
      `<strong>${hit.label}</strong><br/>Screenings: ${hit.count.toLocaleString()}`,
      { sticky: true, className: "dash-map-tip", direction: "top" }
    );
    layer.bindPopup(
      `<strong>${hit.label}</strong><br/>Screenings: ${hit.count.toLocaleString()}`
    );
  };

  const legendBg = colors?.tooltipBg || "rgba(15, 23, 42, 0.88)";
  const legendMuted = colors?.tooltipTextMuted || "rgba(255,255,255,0.55)";

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}>
      <MapContainer
        center={WORLD_CENTER}
        zoom={WORLD_ZOOM}
        minZoom={1}
        maxZoom={8}
        scrollWheelZoom={false}
        style={{
          width: "100%",
          height: "100%",
          background: mode === "dark" ? "#0a1120" : "#eef2f7",
        }}
      >
        <TileLayer url={TILE_URL[mode]} />
        <TileLayer url={TILE_LABELS_URL[mode]} />
        <InvalidateSizeOnMount />

        {borders && (
          <GeoJSON
            key={`borders-${mode}-${byCountry.size}-${Array.from(byCountry.values()).reduce((s, v) => s + v.count, 0)}`}
            data={borders}
            style={styleFeature}
            onEachFeature={onEachFeature}
          />
        )}

        {pins.map((p) => (
          <Marker key={p.key} position={[p.lat, p.lng]} icon={buildOriginPinIcon(p.count)}>
            <Tooltip direction="top" offset={[0, -28]} opacity={1} className="dash-map-tip">
              <div>
                <strong>{p.name}</strong>
                <br />
                Screenings: {p.count.toLocaleString()}
              </div>
            </Tooltip>
            <Popup>
              <Box sx={{ minWidth: 120 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{p.name}</Typography>
                <Typography sx={{ fontSize: 12 }}>Screenings: {p.count.toLocaleString()}</Typography>
              </Box>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      <Box
        sx={{
          position: "absolute",
          left: 14,
          bottom: 14,
          zIndex: 500,
          background: legendBg,
          px: 1.5,
          py: 1.1,
          borderRadius: "10px",
          boxShadow: "0 8px 24px rgba(0,0,0,.3)",
          minWidth: 160,
        }}
      >
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: legendMuted, letterSpacing: 0.5, mb: 0.8, textTransform: "uppercase" }}>
          Screenings
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.8 }}>
          {CHORO_ALPHAS.map((a) => (
            <Box
              key={a}
              sx={{
                flex: 1,
                height: 8,
                borderRadius: "2px",
                background: `${CHORO_BASE}${a}`,
              }}
            />
          ))}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}>
          <Typography sx={{ fontSize: 9, color: legendMuted }}>Low</Typography>
          <Typography sx={{ fontSize: 9, color: legendMuted }}>High</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: RISK_COLORS.kenya, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.78)" }}>Kenya (destination)</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default GeoDashboardMap;
